import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession, readPendingCheckout } from '../lib/auth';
import { useUserOrg } from '../lib/org';
import { getBillingSnapshot, BillingSnapshot } from '../lib/billing';

/**
 * Route guards for everything behind the paywall.
 *
 * Terms of Service §3 commits us to a card-first model with a 30-day free trial
 * (no charge until day 30). So every customer-facing route should be gated by an active
 * subscription — without this gate (which is how the app shipped before
 * 11 Jun 2026) anyone who signed up could use the app indefinitely for free.
 *
 * IMPLEMENTATION NOTE — why we don't use react-router-dom's <Navigate>:
 *
 *   <Navigate>'s internal useEffect has NO dependency array, so it calls
 *   `navigate(to, { replace: true })` on EVERY render of the Navigate
 *   component. If a parent of the guard re-renders frequently (auth state
 *   churn, useUserOrg refreshing, billing snapshot updates), Navigate fires
 *   replaceState() repeatedly and trips Chrome's "100 calls per 10 seconds"
 *   security throttle. Sentry HS-SAAS-APP-8/9 both fired with this exact
 *   stack trace.
 *
 *   Instead we compute a target route deterministically from the auth +
 *   billing state, then drive navigate() from a useEffect whose deps array
 *   contains only the *value* of the target. navigate() only fires when the
 *   computed target actually changes, not on every render. Bulletproof
 *   against re-render storms.
 *
 * Status semantics (mirrors BillingSnapshot.status in lib/billing.ts):
 *   - active     → in. The customer is paying.
 *   - trial      → in. Stripe/Apple/Google trials arrive as 'active'; if Stripe is
 *                  configured with a trial period or we re-introduce one, this
 *                  keeps the gate open.
 *   - past_due   → in IF the grace window hasn't closed yet (gracePeriodEndsAt
 *                  in the future), otherwise out. Lets us soft-fail card retries
 *                  without locking the customer out mid-shift.
 *   - cancelled  → in until the paid period ends (currentPeriodEnd in future).
 *   - paused     → out.
 *   - any other  → out (defensive — unknown states fall through to paywall).
 *
 * Routes that MUST stay accessible even when out (so the customer can recover):
 *   - /paywall, /settings, /settings/plan — handled at the route level.
 */
export function RequireSubscription({ children }: { children: ReactNode }) {
  // sessionLoading is CRITICAL — useSession's initial state is { session: null,
  // loading: true } until supabase.auth.getSession() resolves. Without checking
  // loading, we'd see session=null on first render after sign-in (because this
  // component's own useSession instance hasn't received the SIGNED_IN event yet
  // from supabase's async listener) and incorrectly redirect to /login. That
  // produced the "login takes me back to login" race observed on two phones
  // 11 Jun 2026 — phones with a cached session worked fine, but a fresh device
  // hit the race on every sign-in. Failing closed during loading == waiting.
  const { session, loading: sessionLoading } = useSession();
  // org.org_id is the only field we actually depend on, so we read it once
  // and use it in the effect deps. Pulling the whole org object into a deps
  // array would re-fire the effect on every render of useUserOrg's parent.
  const { org, loading: orgLoading } = useUserOrg();
  const orgId = org?.org_id ?? null;
  const [billing, setBilling] = useState<BillingSnapshot | null>(null);
  const [billingLoading, setBillingLoading] = useState(true);
  // Pending-checkout probe. Set to true when we've resolved that the user
  // has a plan stashed from the marketing pricing page and should be routed
  // to /checkout instead of /paywall. Runs once per session-signed-in
  // transition; readPendingCheckout is cheap (user_metadata lives on the
  // in-memory session, localStorage lookup is sync).
  const [hasPendingCheckout, setHasPendingCheckout] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!orgId) {
      setBillingLoading(false);
      return;
    }
    let cancelled = false;
    setBillingLoading(true);
    (async () => {
      try {
        const b = await getBillingSnapshot(orgId);
        if (!cancelled) setBilling(b);
      } catch {
        // Fail-closed: if we can't read billing status, send the user to
        // the paywall rather than letting them through. They can always
        // refresh from there.
        if (!cancelled) setBilling(null);
      } finally {
        if (!cancelled) setBillingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  // Pending-checkout resolution — see task #142. If the user landed here
  // fresh from marketing → signup → email-verify, we route them to
  // /checkout for their chosen plan instead of dumping them at /paywall.
  // Read once per session; depends on session presence only, not on the
  // billing snapshot (we've already decided we're not paying-active if
  // this branch is going to fire).
  const hasSession = !!session;
  useEffect(() => {
    if (!hasSession) {
      setHasPendingCheckout(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const pc = await readPendingCheckout();
      if (!cancelled) setHasPendingCheckout(!!pc);
    })();
    return () => { cancelled = true; };
  }, [hasSession]);

  // Compute target route deterministically. `null` means "stay here, render
  // children". A string means "redirect here once via useEffect below".
  // Order matters: ALWAYS check sessionLoading BEFORE the session-null branch.
  // If we redirect to /login on a still-loading session, fresh devices get
  // bounced back to the login page right after a successful sign-in.
  const target: string | null = (() => {
    if (sessionLoading) return null; // wait for the session check to resolve
    if (!session) return '/login';
    if (orgLoading || billingLoading) return null; // still loading — render nothing
    // Wait for the pending-checkout probe to settle before deciding
    // between /paywall and /checkout — otherwise a fresh post-verify
    // user gets a flash of /paywall before we route to /checkout.
    if (hasPendingCheckout === null) return null;
    if (!org) return hasPendingCheckout ? '/checkout' : '/paywall';
    if (!billing || !isSubscriptionActive(billing)) {
      return hasPendingCheckout ? '/checkout' : '/paywall';
    }
    return null; // allowed
  })();

  useEffect(() => {
    // Belt-and-braces: only navigate if we're not already at the target.
    // Combined with SessionProvider eliminating the cascading-listener
    // render loop, this is now defensive rather than essential — but it
    // costs nothing and protects against any future regression.
    if (target && window.location.pathname !== target) {
      navigate(target, { replace: true });
    }
    // navigate is intentionally NOT in the deps array — useNavigate()
    // returns a new function ref on every render in non-data-router
    // setups (BrowserRouter), so depending on it re-fires the effect
    // every render and fires replaceState in a tight loop (Sentry
    // HS-SAAS-APP-A, 11 Jun 2026).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  // While loading or while we have a pending redirect, render nothing.
  // SplashGate's loading state covers the visible UX during this gap.
  if (target !== null) return null;
  if (sessionLoading || orgLoading || billingLoading) return null;
  return <>{children}</>;
}

/**
 * Variant that only requires a signed-in session — used for /paywall,
 * /settings, /settings/plan so the customer can subscribe or manage billing
 * even when their subscription has lapsed.
 *
 * Uses the same useNavigate-in-useEffect pattern as RequireSubscription
 * to avoid the <Navigate> render-loop trap.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { session, loading } = useSession();
  const navigate = useNavigate();
  const needsLogin = !loading && !session;

  useEffect(() => {
    if (needsLogin && window.location.pathname !== '/login') {
      navigate('/login', { replace: true });
    }
    // Same reason as in RequireSubscription — navigate is not a stable
    // reference in non-data-router setups; leaving it out of the deps
    // array is intentional. See the comment there for the full Sentry
    // forensic trail.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsLogin]);

  if (loading) return null;
  if (needsLogin) return null;
  return <>{children}</>;
}

/** Returns true when the org should be granted access to the gated routes. */
function isSubscriptionActive(b: BillingSnapshot): boolean {
  const now = Date.now();
  switch (b.status) {
    case 'active':
      return true;
    // 'unpaid' = signed up but never subscribed (default for new companies
    // since 28 Sep 2026). 'trial' is legacy — before that date every new
    // company was created as 'trial' and this gate let them in for free
    // forever. Free trials now run through Stripe/Apple/Google (card first, 30 days) and arrive as 'active'.
    case 'unpaid':
    case 'trial':
      return false;
    case 'past_due':
      // Grace window: while Stripe is retrying the card, keep the customer
      // in. Out the moment the grace period has elapsed.
      return !!b.gracePeriodEndsAt && new Date(b.gracePeriodEndsAt).getTime() > now;
    case 'cancelled':
      // Cancelled but still inside the paid period — they retain access
      // until the period ends, as Terms §4.4 promises.
      return !!b.currentPeriodEnd && new Date(b.currentPeriodEnd).getTime() > now;
    case 'paused':
      return false;
    default:
      return false;
  }
}
